import React from "react";
import {Box, ButtonBase, Divider, MenuItem, Select, Stack, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon} from "@hugeicons/core-free-icons";
import {ProjectRoles} from "../../context/AppContext";
import {useRegistryProject} from "./RegistryProjectContext";

// A sub project as shown in pickers: live dot, name, and its language code
export const SubProjectOption = ({subProject}) => (
  <Stack direction={"row"} alignItems={"center"} gap={0.75} sx={{minWidth: 0}}>
    <Box component={"span"} sx={{
      width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: subProject.live ? "#22C55E" : "#C9CAD8",
    }}/>
    <Typography noWrap component={"span"} sx={{fontSize: 12, fontWeight: 600}}>
      {subProject.name || subProject.languageName || subProject.languageCode}
    </Typography>
    {subProject.languageCode && (
      <Typography
        component={"span"} title={subProject.languageName}
        sx={{fontSize: 10, fontWeight: 600, color: "#8A8AA0", textTransform: "uppercase", flexShrink: 0}}
      >
        {subProject.languageCode}
      </Typography>
    )}
  </Stack>
);

const ALL = "__all__";
const ADD = "__add__";

// Roles that may open the project settings, where sub projects are added
const SETTINGS_ROLES = [ProjectRoles.ADMIN, ProjectRoles.MANAGER];

// Settings tab that lists the sub projects; ?add=1 opens its "Add sub project" dialog
export const subProjectSettingsPath = (basePath, add) => `${basePath}/settings?tab=sub-projects${add ? "&add=1" : ""}`;

// Heading filter look: white with a border while it matches everything, accent tint once a value is picked
export const filterSelectSx = (selected) => ({
  height: 30, maxWidth: 200, borderRadius: "999px", px: 1.25, fontSize: 12,
  color: selected ? "#4B55C9" : "#4A4A63",
  bgcolor: selected ? "rgba(132, 145, 247, 0.12)" : "#fff",
  border: selected ? "1px solid transparent" : "1px solid #E4E4EE",
  "& .MuiSelect-select": {py: 0, pr: "20px !important", display: "flex", alignItems: "center"},
  "& .MuiSelect-icon": {fontSize: 18, right: 4, color: selected ? "#4B55C9" : "#8A8AA0"},
});

const filterLabel = (text) => <Typography noWrap component={"span"} sx={{fontSize: 12, fontWeight: 600}}>{text}</Typography>;

// Version the conversations ran on (initParams PROJECT_VERSION_TYPE); TEST conversations come from the test chat
export const VERSION_TYPES = [
  {value: "PRODUCTION", label: "Production", color: "#1E8A55"},
  {value: "STAGE", label: "Stage", color: "#6E5BC4"},
];

// Production / Stage switch of a page's statistics (segmented control, one is always picked)
export const VersionTypeSwitch = ({value, onChange, sx}) => (
  <Stack role={"radiogroup"} aria-label={"Version type"} direction={"row"} sx={{
    height: 30, p: 0.25, gap: 0.25, borderRadius: "999px", bgcolor: "#fff", border: "1px solid #E4E4EE", ...sx,
  }}>
    {VERSION_TYPES.map(item => {
      const selected = item.value === value;
      return (
        <ButtonBase
          key={item.value}
          role={"radio"}
          aria-checked={selected}
          onClick={() => !selected && onChange(item.value)}
          sx={{
            gap: 0.625, px: 1.125, borderRadius: "999px", fontSize: 12, fontWeight: 600,
            color: selected ? "#4B55C9" : "#6B6B80", bgcolor: selected ? "rgba(132, 145, 247, 0.14)" : "transparent",
            transition: "background-color .15s ease, color .15s ease",
            "&:hover": {color: selected ? "#4B55C9" : "#23264D", bgcolor: selected ? "rgba(132, 145, 247, 0.18)" : "#F4F4FA"},
          }}
        >
          <Box component={"span"} sx={{width: 6, height: 6, borderRadius: "50%", flexShrink: 0, bgcolor: item.color,
            opacity: selected ? 1 : 0.5}}/>
          {item.label}
        </ButtonBase>
      );
    })}
  </Stack>
);

// Sub project filter of a page's statistics: "All sub projects" (null) or one sub project of the project.
// value: the selected sub project id, or null for every sub project. Does not change the URL sub project.
export const SubProjectFilter = ({value, onChange, sx}) => {
  const {project} = useRegistryProject();
  const selected = project.subProjects.find(item => String(item.id) === String(value));

  return (
    <Select
      variant={"standard"}
      disableUnderline
      value={selected ? String(selected.id) : ALL}
      onChange={(e) => onChange(e.target.value === ALL ? null : e.target.value)}
      renderValue={() => selected ? <SubProjectOption subProject={selected}/> : filterLabel("All sub projects")}
      inputProps={{"aria-label": "Sub project"}}
      sx={{...filterSelectSx(Boolean(selected)), ...sx}}
      MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
    >
      <MenuItem value={ALL} dense>{filterLabel("All sub projects")}</MenuItem>
      {project.subProjects.map(item => (
        <MenuItem key={item.id} value={String(item.id)} dense>
          <SubProjectOption subProject={item}/>
        </MenuItem>
      ))}
    </Select>
  );
};

// Sub project switcher for the pages that work on one sub project (designer, dialplan). The sub project is part
// of the URL, so picking one opens the same page for it. Admins and managers also get "Add sub project", which
// opens the sub projects settings. size "medium" fits page headings.
export const SubProjectSelect = ({size = "small", sx}) => {
  const {project, subProject, role, basePath, changeSubProject} = useRegistryProject();
  const navigate = useNavigate();
  const medium = size === "medium";
  const canAdd = SETTINGS_ROLES.includes(role);

  const handleChange = (value) => {
    if (value === ADD) navigate(subProjectSettingsPath(basePath, true));
    else changeSubProject(value);
  };

  return (
    <Select
      variant={"standard"}
      disableUnderline
      value={String(subProject.id)}
      onChange={(e) => handleChange(e.target.value)}
      renderValue={() => <SubProjectOption subProject={subProject}/>}
      inputProps={{"aria-label": "Sub project"}}
      sx={{
        maxWidth: medium ? 240 : 190, borderRadius: "999px", px: medium ? 1.5 : 1.25, color: "#4B55C9",
        bgcolor: "rgba(132, 145, 247, 0.12)", ...(medium && {height: 42}),
        "& .MuiSelect-select": {py: 0.5, pr: "22px !important", display: "flex", alignItems: "center"},
        "& .MuiSelect-icon": {color: "#4B55C9"},
        ...sx,
      }}
      MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
    >
      {project.subProjects.map(item => (
        <MenuItem key={item.id} value={String(item.id)} dense>
          <SubProjectOption subProject={item}/>
        </MenuItem>
      ))}
      {canAdd && <Divider/>}
      {canAdd && (
        <MenuItem value={ADD} dense sx={{gap: 0.75, color: "#4B55C9"}}>
          <HugeiconsIcon icon={Add01Icon} size={14}/>
          <Typography component={"span"} sx={{fontSize: 12, fontWeight: 600}}>Add sub project</Typography>
        </MenuItem>
      )}
    </Select>
  );
};
